import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Search, ArrowRight, Sparkles, ShieldCheck, Store, PlayCircle, Award, MapPin,
  CalendarCheck, ListPlus, ShoppingBag, Star, Wallet, Ruler, Volume2, PartyPopper,
  Timer, Clock, Zap, Loader2, CheckCircle2, Flame, Lightbulb, Moon,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { GlassCard, SectionTitle } from "../components/common/ui";
import FireworksCanvas from "../components/common/FireworksCanvas";
import StaticFirework from "../components/common/StaticFirework";
import api, { formatApiError } from "../lib/api";
import { IMG } from "../config/brand";
import { SeasonalBanner, HomeCollections, PopularProducts, CurrentOffers } from "../components/common/HomeSections";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/1b6a2df0-aad5-473f-8025-00bad8c17ce1/images/dd26607a7c1ded490b6b24414101c6c70aba9026452131f9e2fd4e23d621e67a.png";

const WHY = [
  { icon: CalendarCheck, title: "Reserve Online", text: "Secure your fireworks in minutes — no payment needed to reserve." },
  { icon: Store, title: "Pay In Store", text: "Only pay when you collect, in person, at our local store." },
  { icon: PlayCircle, title: "Demonstration Videos", text: "Watch every firework perform before you decide." },
  { icon: Award, title: "Premium Brands", text: "Hand-picked, top-tier fireworks from trusted names." },
  { icon: MapPin, title: "Local Collection", text: "Convenient Click & Collect near Clacton-on-Sea, Essex." },
  { icon: ShieldCheck, title: "Challenge 25 Retailer", text: "A responsible retailer. Proof of age required on collection." },
];

const CATEGORIES = [
  { name: "Selection Boxes", img: IMG.selectionBox },
  { name: "Barrages", img: IMG.barrage },
  { name: "Rockets", img: IMG.rocket },
  { name: "Fountains", img: IMG.fountain },
  { name: "Sparklers", img: IMG.sparklers },
  { name: "Low Noise", img: IMG.familyBlue },
  { name: "Family Fireworks", img: IMG.familyCouple },
  { name: "Display Packs", img: IMG.bigDisplay },
];

const PRODUCTS = [
  { name: "Celestial Finale Selection Box", price: "£49.99", shots: "42", duration: "90s", noise: "Loud", distance: "25m", img: IMG.selectionBox, stock: "In Stock", promo: "Best Seller" },
  { name: "Golden Storm Barrage", price: "£89.99", shots: "100", duration: "120s", noise: "Loud", distance: "25m", img: IMG.barrage, stock: "In Stock", promo: "New" },
  { name: "Skyline Rocket Pack", price: "£34.99", shots: "12", duration: "45s", noise: "Medium", distance: "20m", img: IMG.rocket, stock: "Low Stock", promo: null },
  { name: "Silver Fountain Trio", price: "£19.99", shots: "3", duration: "60s", noise: "Low", distance: "8m", img: IMG.fountain, stock: "In Stock", promo: "Low Noise" },
];

const STEPS = [
  { icon: ShoppingBag, title: "Browse Fireworks", text: "Explore the range and watch demonstration videos." },
  { icon: ListPlus, title: "Add to Reservation", text: "Build your list — no payment required." },
  { icon: CalendarCheck, title: "Reserve Online", text: "Choose your collection date and confirm." },
  { icon: Store, title: "Collect & Pay In Store", text: "Pay on collection. Challenge 25 applies." },
];

const TOWNS = ["Clacton-on-Sea", "Weeley", "Frinton-on-Sea", "Holland-on-Sea", "Brightlingsea", "Great Bentley", "Thorpe-le-Soken", "Essex"];

const OCCASIONS = [
  { icon: Flame, title: "Bonfire Night Fireworks", text: "Plan your 5th of November display.", to: "/bonfire-night-fireworks" },
  { icon: PartyPopper, title: "New Year's Eve Fireworks", text: "Welcome in the New Year at midnight.", to: "/new-years-eve-fireworks" },
  { icon: Lightbulb, title: "Diwali Fireworks", text: "Colour and light for the festival.", to: "/diwali-fireworks" },
  { icon: Moon, title: "Chinese New Year Fireworks", text: "Mark the Lunar New Year.", to: "/chinese-new-year-fireworks" },
];

const STOCK_STYLES = {
  "In Stock": "bg-emerald-500/15 text-emerald-400",
  "Low Stock": "bg-amber/15 text-amber",
  "Out of Stock": "bg-firered/15 text-firered",
};

const fade = { hidden: { opacity: 0, y: 24 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.06 } }) };

const vipInp = "w-full rounded-lg bg-black/40 border border-white/15 text-white placeholder:text-white/40 px-3 py-2.5 text-sm focus:outline-none focus:border-gold/50";

function Countdown({ target, title, subtitle }) {
  const compute = () => {
    if (!target) return null;
    const diff = new Date(target).getTime() - Date.now();
    if (diff <= 0) return null;
    return {
      d: Math.floor(diff / 86400000),
      h: Math.floor((diff / 3600000) % 24),
      m: Math.floor((diff / 60000) % 60),
      s: Math.floor((diff / 1000) % 60),
    };
  };
  const [t, setT] = useState(compute);
  useEffect(() => {
    const id = setInterval(() => setT(compute()), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
      <GlassCard className="p-8 sm:p-12 relative overflow-hidden" hover={false} data-testid="countdown-section">
        <div className="absolute -top-24 -left-16 h-56 w-56 rounded-full bg-fireorange/10 blur-3xl" />
        <div className="relative flex flex-col lg:flex-row items-center justify-between gap-8">
          <div className="text-center lg:text-left">
            <p className="text-xs tracking-[0.3em] uppercase font-bold cf-gold-text flex items-center gap-2 justify-center lg:justify-start"><Timer className="h-4 w-4" /> {subtitle || "Seasonal"}</p>
            <h3 className="mt-3 font-playfair text-2xl sm:text-3xl text-white">{title}</h3>
          </div>
          {t ? (
            <div className="flex items-center gap-3 sm:gap-4">
              {[["Days", t.d], ["Hrs", t.h], ["Min", t.m], ["Sec", t.s]].map(([label, val]) => (
                <div key={label} className="text-center">
                  <div className="grid place-items-center h-16 w-16 sm:h-20 sm:w-20 rounded-2xl bg-navy border border-gold/20 font-cinzel text-2xl sm:text-3xl text-gold tabular-nums">
                    {String(val).padStart(2, "0")}
                  </div>
                  <span className="mt-2 block text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
                </div>
              ))}
            </div>
          ) : (
            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-500/15 text-emerald-400 px-5 py-3 text-sm font-semibold"><Zap className="h-4 w-4" /> Now Accepting Reservations</span>
          )}
        </div>
      </GlassCard>
    </section>
  );
}

function BuyingDates() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/collection/periods").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data || !(data.periods || []).length) return null;
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16" data-testid="buying-dates">
      <SectionTitle eyebrow="Collection Periods" title={data.heading} subtitle={data.note} />
      <div className="mt-10 grid gap-5 sm:grid-cols-2 max-w-3xl mx-auto">
        {data.periods.map((p, i) => (
          <motion.div key={i} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} custom={i} variants={fade}>
            <GlassCard className="p-7 flex items-center gap-4" data-testid={`buying-date-${i}`}>
              <div className="grid place-items-center h-12 w-12 rounded-xl bg-gold/10 border border-gold/20 shrink-0">
                <CalendarCheck className="h-6 w-6 text-gold" />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wider text-white/40">Permitted period</p>
                <p className="font-playfair text-xl text-white mt-0.5">{p.label}</p>
              </div>
            </GlassCard>
          </motion.div>
        ))}
      </div>
    </section>
  );
}


export default function Landing() {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [vipForm, setVipForm] = useState({ name: "", email: "", mobile: "", marketing_consent: false });
  const [vipLoading, setVipLoading] = useState(false);
  const [vipDone, setVipDone] = useState(false);
  const [vipDoneMsg, setVipDoneMsg] = useState("");
  const [vipError, setVipError] = useState("");

  const submitVip = async (e) => {
    e.preventDefault();
    setVipError("");
    if (!vipForm.name.trim() || !vipForm.email.trim()) { setVipError("Name and email are required."); return; }
    if (!vipForm.marketing_consent) { setVipError("Please tick the marketing consent box to join and receive your 10% coupon."); return; }
    setVipLoading(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", { ...vipForm, source_page: "homepage" });
      setVipDoneMsg(data?.message || "");
      setVipDone(true);
    } catch (err) {
      setVipError(formatApiError(err.response?.data?.detail) || "Something went wrong.");
    } finally { setVipLoading(false); }
  };

  useEffect(() => {
    api.get("/content/homepage").then(({ data }) => setContent(data)).catch(() => setContent({}));
  }, []);

  const hero = content?.hero || {};
  const vip = content?.vip || {};
  const countdown = content?.countdown || {};

  const defaultTarget = useMemo(() => {
    const now = new Date();
    let year = now.getFullYear();
    if (now > new Date(`${year}-11-05T18:00:00`)) year += 1;
    return `${year}-11-05T18:00:00`;
  }, []);

  return (
    <div>
      {/* HERO — cinematic centered gold lockup */}
      <section className="relative overflow-hidden min-h-[66vh] flex items-center justify-center py-20" data-testid="hero-section">
        {/* Base gold-firework imagery */}
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_BG})` }} />
        {/* Slight darkening (~13%) so the metallic wordmark stands out */}
        <div className="absolute inset-0 bg-black/[0.13] pointer-events-none" />
        {/* Subtle live sparks over the still image */}
        <div className="absolute inset-0 opacity-50 pointer-events-none"><FireworksCanvas className="absolute inset-0 h-full w-full" /></div>
        {/* Persistent gold burst on the right for image-led impact */}
        <div className="absolute inset-0 hidden md:block pointer-events-none" aria-hidden="true"><StaticFirework cx={85} cy={30} size={320} opacity={0.7} /></div>
        {/* Readability + luxury vignette */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(820px 560px at 50% 46%, rgba(5,7,13,0.62) 0%, rgba(5,7,13,0.5) 58%, rgba(5,7,13,0.7) 100%)" }} />
        <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: "inset 0 0 200px 30px rgba(0,0,0,0.7)" }} />
        {/* thin gold frame like the reference */}
        <div className="absolute inset-3 sm:inset-5 rounded-2xl border border-gold/25 pointer-events-none" />

        <div className="relative mx-auto max-w-4xl px-6 w-full text-center">
          <motion.div initial="hidden" animate="show">
            {/* Eyebrow */}
            <motion.div variants={fade} custom={0} className="flex items-center justify-center gap-3 mb-6" data-testid="hero-eyebrow">
              <span className="h-px w-8 bg-gradient-to-r from-transparent to-gold" />
              <Sparkles className="h-4 w-4 text-gold" />
              <p className="font-cinzel uppercase tracking-[0.32em] text-[11px] sm:text-xs text-gold/90">
                {hero.eyebrow || "Near Clacton-on-Sea · Essex"}
              </p>
              <span className="h-px w-8 bg-gradient-to-l from-transparent to-gold" />
            </motion.div>

            {/* Brand lockup — logo-matched metallic wordmark (SEO-readable) */}
            <motion.div variants={fade} custom={1}>
              <h1 className="font-cinzel font-bold uppercase text-center leading-[0.95] tracking-[0.13em] drop-shadow-[0_2px_30px_rgba(0,0,0,0.7)]" data-testid="hero-title">
                <span className="block cf-silver-text cf-animate-shimmer text-5xl sm:text-6xl lg:text-7xl">CLACTON</span>
                <span className="block cf-gold-metallic cf-animate-shimmer text-4xl sm:text-5xl lg:text-6xl mt-1.5 sm:mt-2.5" data-testid="hero-brand-fireworks">FIREWORKS</span>
              </h1>
            </motion.div>

            <motion.p variants={fade} custom={3} className="mt-6 font-playfair font-semibold text-lg sm:text-xl cf-gold-sheen" data-testid="hero-subheadline">
              {hero.title2 || "Premium Fireworks for Clacton-on-Sea, Weeley & Surrounding Areas"}
            </motion.p>

            {/* HERO VIP CALL-TO-ACTION (clickable) */}
            <motion.div variants={fade} custom={5} className="mt-9 flex justify-center">
              <button
                type="button"
                onClick={() => navigate("/vip")}
                data-testid="hero-vip-cta"
                className="group relative w-full max-w-xl text-left rounded-2xl border border-gold/40 bg-black/40 backdrop-blur px-6 py-5 sm:px-8 sm:py-6 hover:border-gold hover:bg-gold/10 transition-colors shadow-[0_10px_30px_-8px_rgba(232,181,75,0.4)]"
              >
                <div className="flex items-center gap-2 text-gold">
                  <Sparkles className="h-4 w-4" />
                  <span className="text-[11px] font-bold uppercase tracking-[0.28em]">Free to Join</span>
                </div>
                <h2 className="mt-2 font-playfair text-xl sm:text-2xl text-white leading-snug">
                  Join the Clacton Fireworks VIP List
                </h2>
                <div className="mt-4 flex items-center justify-between gap-4">
                  <span className="inline-flex items-baseline gap-2">
                    <span className="font-cinzel font-bold text-2xl sm:text-3xl cf-gold-metallic leading-none">10% OFF</span>
                    <span className="text-sm text-white/80">your first order</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-gold font-semibold text-sm group-hover:gap-2.5 transition-all">
                    Join now <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </button>
            </motion.div>

            {/* Reassurance */}
            <motion.div variants={fade} custom={7} className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-white/45">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-gold/70" /> 18+ only · Challenge 25 applies</span>
              <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-gold/70" /> Convenient local collection near Clacton-on-Sea</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* SELLING SEASON BANNER (Current Season) */}
      <SeasonalBanner />

      {/* COUNTDOWN (CMS-configurable) */}
      {(countdown.enabled ?? true) && (
        <Countdown
          target={countdown.target ?? defaultTarget}
          title={countdown.title || "Bonfire Night Collection Opens In"}
          subtitle={countdown.subtitle}
        />
      )}

      {/* PROMOTIONS FIRST, THEN SHOP BY CATEGORY */}
      <CurrentOffers />
      <HomeCollections />
      <PopularProducts />

      {/* Fireworks Buying & Collection Dates */}
      <BuyingDates />

      {/* SHOP BY OCCASION — links to cornerstone seasonal guides */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16" data-testid="shop-by-occasion">
        <SectionTitle eyebrow="By Occasion" title="Fireworks by Occasion" subtitle="Planning for a celebration? Explore our guides to choosing the right fireworks." />
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {OCCASIONS.map((o, i) => (
            <motion.div key={o.title} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} custom={i} variants={fade}>
              <Link to={o.to} data-testid={`occasion-card-${i}`} className="group block h-full">
                <GlassCard className="p-7 h-full flex flex-col">
                  <div className="grid place-items-center h-12 w-12 rounded-xl bg-gold/10 border border-gold/20 group-hover:bg-gradient-to-br group-hover:from-amber group-hover:to-fireorange transition-all duration-300">
                    <o.icon className="h-6 w-6 text-gold group-hover:text-ink transition-colors" />
                  </div>
                  <h3 className="mt-5 font-playfair text-lg text-white">{o.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed flex-1">{o.text}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-gold font-semibold text-sm group-hover:gap-2.5 transition-all">
                    View guide <ArrowRight className="h-4 w-4" />
                  </span>
                </GlassCard>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* WHY CHOOSE US — placed after products so customers browse first */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
        <SectionTitle eyebrow="Why Us" title="Why Choose Clacton Fireworks" subtitle="A premium, responsible, local fireworks experience — from browse to collection." />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {WHY.map((w, i) => (
            <motion.div key={w.title} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} custom={i} variants={fade}>
              <GlassCard className="p-7 h-full group" data-testid={`why-card-${i}`}>
                <div className="grid place-items-center h-12 w-12 rounded-xl bg-gold/10 border border-gold/20 group-hover:bg-gradient-to-br group-hover:from-amber group-hover:to-fireorange transition-all duration-300">
                  <w.icon className="h-6 w-6 text-gold group-hover:text-ink transition-colors" />
                </div>
                <h3 className="mt-5 font-playfair text-lg text-white flex items-center gap-2">{w.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{w.text}</p>
              </GlassCard>
            </motion.div>
          ))}
        </div>
      </section>

      {/* TRUST / REVIEWS */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
        <SectionTitle eyebrow="Reviews" title="Loved By Locals" subtitle="Verified customer reviews will appear here." align="center" className="mx-auto text-center" />
        <GlassCard className="mt-10 p-12 flex flex-col items-center text-center" hover={false} data-testid="trust-section">
          <div className="flex items-center gap-1 text-gold">
            {Array.from({ length: 5 }).map((_, i) => <Star key={i} className="h-6 w-6 fill-gold" />)}
          </div>
          <p className="mt-5 font-playfair text-xl text-white">Launching Soon</p>
          <p className="mt-2 text-sm text-muted-foreground max-w-md">Google Reviews will appear here after launch. Be one of our first reviewers.</p>
        </GlassCard>
      </section>

      {/* VIP — large background image campaign banner with inline capture form */}
      {(vip.enabled ?? true) && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
          <div className="relative overflow-hidden rounded-3xl border border-white/10 p-8 sm:p-12" data-testid="vip-section">
            {vip.background && <img src={vip.background} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />}
            <div className="absolute inset-0" style={{ background: "radial-gradient(120% 120% at 30% 30%, rgba(5,7,13,0.6), rgba(5,7,13,0.92))" }} />
            <div className="relative grid lg:grid-cols-2 gap-8 lg:gap-10 items-center">
              <div className="text-center lg:text-left">
                <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3.5 py-1 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.2em] text-gold" data-testid="vip-free-badge">
                  <Sparkles className="h-3.5 w-3.5" /> 100% Free to Join
                </span>
                <div className="mt-4 flex items-end justify-center lg:justify-start gap-2 sm:gap-3" data-testid="vip-offer-highlight">
                  <span className="font-cinzel font-bold text-6xl sm:text-7xl lg:text-8xl cf-gold-metallic leading-[0.8]">10%</span>
                  <span className="font-cinzel font-bold text-2xl sm:text-3xl lg:text-4xl cf-gold-metallic leading-none pb-1 sm:pb-2">OFF</span>
                </div>
                <p className="mt-2 text-xs sm:text-sm uppercase tracking-[0.28em] text-white/70">Your First Order</p>
                <h2 className="mt-5 font-playfair text-2xl sm:text-3xl lg:text-4xl text-white">{vip.title || "Join Our VIP List"}</h2>
                <p className="mt-3 mx-auto lg:mx-0 max-w-md text-sm sm:text-base text-white/75 leading-relaxed">{vip.text || "Sign up today and receive 10% OFF your first order. Be the first to hear about exclusive offers, new arrivals and when the next fireworks season begins."}</p>
                <p className="mt-3 text-xs text-white/45">Free to join — no membership fees. Unsubscribe any time.</p>
              </div>
              {vipDone ? (
                <div className="text-center lg:text-left" data-testid="vip-inline-success">
                  <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto lg:mx-0" />
                  <p className="mt-3 font-playfair text-2xl text-white">You're on the list!</p>
                  <p className="mt-1 text-white/70">{vipDoneMsg || "We'll be in touch with early access and exclusive offers."}</p>
                </div>
              ) : (
                <form onSubmit={submitVip} className="rounded-2xl border border-white/10 bg-black/40 backdrop-blur p-6 space-y-3" data-testid="vip-inline-form">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <input required value={vipForm.name} onChange={(e) => setVipForm({ ...vipForm, name: e.target.value })} placeholder="Name" className={vipInp} data-testid="vip-inline-name" />
                    <input required type="email" value={vipForm.email} onChange={(e) => setVipForm({ ...vipForm, email: e.target.value })} placeholder="Email" className={vipInp} data-testid="vip-inline-email" />
                  </div>
                  <input value={vipForm.mobile} onChange={(e) => setVipForm({ ...vipForm, mobile: e.target.value })} placeholder="Mobile (optional)" className={vipInp} data-testid="vip-inline-mobile" />
                  <label className="flex items-start gap-2 text-xs text-white/70"><input type="checkbox" className="mt-0.5" checked={vipForm.marketing_consent} onChange={(e) => setVipForm({ ...vipForm, marketing_consent: e.target.checked })} data-testid="vip-inline-consent" required /> I would like to receive seasonal offers, promotions and updates from Clacton Fireworks. I understand that I can unsubscribe at any time. <span className="text-firered">*</span></label>
                  <p className="text-[11px] text-white/40">Get a unique 10% coupon for your first order (valid until 31 Dec 2026). Free to join.</p>
                  {vipError && <p className="text-sm text-firered" data-testid="vip-inline-error">{vipError}</p>}
                  <Button type="submit" disabled={vipLoading} className="cf-gold-btn rounded-full font-semibold w-full h-12 gap-2" data-testid="vip-inline-submit">{vipLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Get 10% OFF</Button>
                </form>
              )}
            </div>
          </div>
        </section>
      )}

      {/* LOCAL SEO */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 border-t border-white/10">
        <div className="max-w-3xl">
          <p className="text-xs tracking-[0.3em] uppercase font-bold cf-gold-text">Local Collection</p>
          <h2 className="mt-3 font-playfair text-2xl text-white">Premium Fireworks Across North Essex</h2>
          <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
            Clacton Fireworks is your local Click & Collect fireworks retailer, proudly serving Clacton-on-Sea and the surrounding Essex area. Reserve online and collect premium rockets, barrages, fountains, selection boxes and low-noise fireworks close to home.
          </p>
        </div>
        <div className="mt-6 flex flex-wrap gap-2" data-testid="local-seo">
          {TOWNS.map((t) => (
            <span key={t} className="rounded-full border border-white/10 bg-navy/60 px-4 py-1.5 text-sm text-white/70 hover:text-gold hover:border-gold/30 transition-colors">{t}</span>
          ))}
        </div>
      </section>
    </div>
  );
}
